import { Stroke, Style } from "ol/style";
import { roadClassColor, withAlpha } from "./hdRoadClass.mjs";

// The two road-class line looks: `roadClassStrokes` for LM lanes, `roadClassBand`
// for RM roads. Same palette, so a lane and the RM road it was built from share a
// colour, but different SHAPES, so the two basemaps stay tellable apart when both
// are on: LM lanes are thin outlined lines (with arrows) on top, the RM road is a
// wide, soft, outline-less band underneath them (the RM row sits a zIndex below).
//
// The lane line gets a dark casing so it reads as a line of its own, not a stroke of
// the background map. Solid on purpose: a dashed casing under
// a dashed fill renders every dash as an outlined bead (round caps on both
// strokes), which reads as a dotted line.
const CASING_COLOR = "#333";
// No class at all (a lane whose road area hasn't loaded, a way without highway=)
// is drawn thin and grey rather than dropped: it is still there, just not
// something this view can say much about.
const UNRESOLVED_COLOR = "#9e9e9e";

/**
 * @param {string|undefined} roadClass highway value, undefined when unknown
 * @param {number} width screen px of the coloured line (casing adds 2)
 * @returns {Style[]}
 */
export function roadClassStrokes(roadClass, width) {
  if (roadClass === undefined) {
    return [
      new Style({ stroke: new Stroke({ color: UNRESOLVED_COLOR, width: 1 }) }),
    ];
  }
  return [
    new Style({
      stroke: new Stroke({ color: CASING_COLOR, width: width + 2 }),
    }),
    new Style({
      stroke: new Stroke({ color: roadClassColor(roadClass), width }),
    }),
  ];
}

// Wide enough to read as the road the lanes sit on, translucent so the lanes and
// the map under it stay visible. No casing: that is the LM lanes' signature.
const BAND_ALPHA = 0.55;

/**
 * @param {string|undefined} roadClass highway value, undefined when unknown
 * @param {number} width screen px of the band
 * @returns {Style[]}
 */
export function roadClassBand(roadClass, width) {
  if (roadClass === undefined) {
    return [
      new Style({ stroke: new Stroke({ color: UNRESOLVED_COLOR, width: 1 }) }),
    ];
  }
  return [
    new Style({
      stroke: new Stroke({
        color: withAlpha(roadClassColor(roadClass), BAND_ALPHA),
        width,
      }),
    }),
  ];
}

// The RM basemap's "Thin wire" view: just the road's centreline, opaque, in its
// class colour, for when the band hides too much of what is under it.
/**
 * @param {string|undefined} roadClass highway value, undefined when unknown
 * @param {number} width screen px of the wire
 * @returns {Style[]}
 */
export function roadClassWire(roadClass, width) {
  return [
    new Style({
      stroke: new Stroke({
        color:
          roadClass === undefined
            ? UNRESOLVED_COLOR
            : roadClassColor(roadClass),
        width,
      }),
    }),
  ];
}
